import {useSelector} from 'react-redux';

import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {selectSaleVehicle} from '@dms/api/vehicle';
import {
  useGetMetadaServiceCaseQuery,
  useGetServiceVehicleAdditionalInformationQuery,
  useGetServiceVehicleQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {testIds, vehiclesRoutes} from '@dms/routes';
import {Section} from '@dms/shared';
import {
  VehicleAdditionalInformation,
  VehicleContentDetails,
  VehicleData,
  VehicleFeatures,
} from '@dms/vehicle-detail';

import {composePath} from 'shared/web';

import {useWorkshopUrl} from '../../../../hooks/useWorkshopUrl';

export function Vehicle() {
  const {serviceCaseId} = useWorkshopUrl();

  const {data: serviceCase} = useGetMetadaServiceCaseQuery({serviceCaseId});

  const {data: saleVehicle} = useSelector(selectSaleVehicle(serviceCase?.vehicleId ?? ''));

  const isSaleVehicle = isNotNil(saleVehicle);

  const {data: serviceVehicle} = useGetServiceVehicleQuery(
    {vehicleId: serviceCase?.vehicleId ?? ''},
    {skip: !serviceCase?.vehicleId}
  );

  const {data: additionalInformation} = useGetServiceVehicleAdditionalInformationQuery(
    {serviceVehicleId: serviceVehicle?.serviceVehicleId ?? ''},
    {skip: !serviceVehicle?.serviceVehicleId}
  );

  const hasAdditionalInformation = !isNilOrEmpty(additionalInformation);

  return (
    <Section
      data-testid={testIds.workshop.serviceCaseDetail('vehicle')}
      tabs={
        serviceCase && serviceCase.vehicleId
          ? [
              {
                queryId: 'basicInformation',
                title: i18n.t('general.labels.basicInformation'),
                content: (
                  <VehicleContentDetails
                    isSaleVehicle={isSaleVehicle}
                    vehicleId={serviceCase.vehicleId}
                  />
                ),
              },
              {
                queryId: 'vehicleData',
                title: i18n.t('entity.vehicle.labels.vehicleData'),
                content: (
                  <VehicleData isSaleVehicle={isSaleVehicle} vehicleId={serviceCase.vehicleId} />
                ),
              },
              {
                queryId: 'features',
                title: i18n.t('entity.vehicle.labels.features'),
                content: <VehicleFeatures vehicleId={serviceCase.vehicleId} />,
              },
              ...(hasAdditionalInformation
                ? [
                    {
                      queryId: 'additionalInformation',
                      title: i18n.t('entity.vehicle.labels.additionalInformation'),
                      content: <VehicleAdditionalInformation vehicleId={serviceCase.vehicleId} />,
                    },
                  ]
                : []),
            ]
          : undefined
      }
      actions={
        serviceCase && serviceCase.vehicleId
          ? [
              {
                type: 'button',

                variant: 'outlined',
                leftIcon: 'action/launch',
                title: i18n.t('entity.vehicle.labels.vehicleDetails'),

                onClick: () =>
                  window.open(
                    composePath(vehiclesRoutes.detail, {params: {id: serviceCase.vehicleId}}),
                    '_blank',
                    'noopener,noreferrer'
                  ),
              },
            ]
          : undefined
      }
    />
  );
}
